import React, { useEffect, useRef, useState } from 'react'

import * as S from './styles'
import { CountUpCard } from '@/components'
import { ReviewCard } from '@/service/api/home'
import { useSmoothScroll } from '@/store/smoothScroll'

export type ReviewContainerProps = {
  /**
   * Numerical card data
   * @example
   * ```tsx
   * <ReviewContainer cards={cards} />
   * ```
   * */
  cards: ReviewCard[]

  /**
   * If it should start counting immediately after rendering
   * @example
   * ```tsx
   * <ReviewContainer cards={cards} />
   * ```
   * */
  countOnRender?: boolean
}

export const ReviewContainer: React.FC<ReviewContainerProps> = ({
  cards,
  countOnRender = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [startCount, setStartCount] = useState(countOnRender)
  const [windowHeight, setWindowHeight] = useState<number | undefined>(
    undefined,
  )
  const { defaultElement, getScrollbar } = useSmoothScroll()

  // hook to handle window height
  useEffect(() => {
    function handleResize() {
      setWindowHeight(window.innerHeight)
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // hook to handle scroll
  useEffect(() => {
    const container = containerRef.current
    const scrollbar = getScrollbar(document.getElementById(defaultElement))

    if (!window || !container || !scrollbar || !windowHeight) return

    const handleWindowScroll = () => {
      if (startCount) return

      const { y: scrollY } = scrollbar.offset

      const isOnContainerViewPort =
        scrollY >= container.offsetTop - windowHeight &&
        scrollY <= container.offsetTop + windowHeight

      if (isOnContainerViewPort) {
        setStartCount(true)
      }
    }

    scrollbar?.addListener(handleWindowScroll)

    // eslint-disable-next-line consistent-return
    return () => {
      scrollbar?.removeListener(handleWindowScroll)
    }
  }, [windowHeight, defaultElement, getScrollbar, startCount])

  if (!cards.length) return null

  return (
    <S.FlexContainerReviews padding="0">
      <S.ReviewContainer ref={containerRef}>
        {cards?.map((card) => (
          <CountUpCard
            key={card.description}
            value={card.value}
            description={card.description}
            prefix={card.prefix}
            suffix={card.suffix}
            startCount={startCount}
          />
        ))}
      </S.ReviewContainer>
    </S.FlexContainerReviews>
  )
}
